<div class="login-page" [class.has-backdrop]="brand.loginBackground">
  <div class="login-backdrop" *ngIf="brand.loginBackground">
    <picture>
      <source srcset="./assets/background.webp" type="image/webp" />
      <img src="./assets/background.jpg" class="login-bg-image" alt="" decoding="async" fetchpriority="low" />
    </picture>
  </div>

  <div class="login-container">
    <div class="login-card">
      <img
        class="login-logo"
        [src]="brand.logo"
        [alt]="brand.productName"
        decoding="async" />
      <h1 class="visually-hidden">{{ brand.productName }}</h1>
      <p class="login-subtitle">Sign in to access your Question Bank</p>

      <div *ngIf="passwordResetOk" class="alert alert-success login-error" role="status">
        <i class="bi bi-check-circle login-error-icon" aria-hidden="true"></i>
        <div class="login-error-body">
          <p class="login-error-text mb-0">Your password was updated. Sign in with your new password.</p>
        </div>
      </div>

      <div *ngIf="signupConfirmed" class="alert alert-success login-error" role="status">
        <i class="bi bi-check-circle login-error-icon" aria-hidden="true"></i>
        <div class="login-error-body">
          <p class="login-error-text mb-0">Your account was confirmed. Sign in to continue.</p>
        </div>
      </div>

      <div *ngIf="brand.showSocialLogin && !runtime.oidcConfigured" class="alert alert-warning login-error" role="alert">
        <i class="bi bi-info-circle login-error-icon" aria-hidden="true"></i>
        <div class="login-error-body">
          <div class="login-error-title">Browser sign-in redirect is not configured</div>
          <p class="login-error-text">
            Add <code>oidcIssuer</code> and <code>oidcClientId</code> for Google or Apple sign-in below, or use email sign-in.
            You can merge a gitignored <code>runtime-config.local.json</code> — copy from <code>runtime-config.example.json</code>.
          </p>
        </div>
      </div>

      <div
        *ngIf="brand.showSocialLogin && runtime.oidcConfigured && oidcDiscovery.errorMessage"
        class="alert alert-danger login-error"
        role="alert">
        <i class="bi bi-exclamation-circle login-error-icon" aria-hidden="true"></i>
        <div class="login-error-body">
          <div class="login-error-title">Browser redirect sign-in is unavailable</div>
          <p class="login-error-text mb-0">{{ oidcDiscovery.errorMessage }}</p>
          <button
            type="button"
            class="btn btn-sm btn-outline-danger mt-2"
            (click)="retryOidcDiscovery()"
            [disabled]="oidcDiscoveryRetrying">
            {{ oidcDiscoveryRetrying ? 'Retrying…' : 'Retry' }}
          </button>
        </div>
      </div>

      <form [formGroup]="emailPasswordForm" (ngSubmit)="onEmailPasswordSubmit()" class="login-form mb-3">
        <div *ngIf="emailPasswordError" class="alert alert-danger login-error" role="alert">
          <i class="bi bi-exclamation-circle login-error-icon" aria-hidden="true"></i>
          <div class="login-error-body">
            <p class="login-error-text mb-0">{{ emailPasswordError }}</p>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label" for="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            class="form-control"
            formControlName="email"
            autocomplete="username"
            placeholder="you@example.com"
            [class.is-invalid]="emailPasswordForm.get('email')?.invalid && emailPasswordForm.get('email')?.touched" />
          <div
            class="invalid-feedback d-block"
            *ngIf="emailPasswordForm.get('email')?.invalid && emailPasswordForm.get('email')?.touched">
            Use an email address such as name&#64;example.com.
          </div>
        </div>

        <div class="form-group">
          <label class="form-label" for="login-password">Password</label>
          <input
            id="login-password"
            type="password"
            class="form-control"
            formControlName="password"
            autocomplete="current-password"
            placeholder="Password"
            [class.is-invalid]="emailPasswordForm.get('password')?.invalid && emailPasswordForm.get('password')?.touched" />
          <div
            class="invalid-feedback d-block"
            *ngIf="emailPasswordForm.get('password')?.invalid && emailPasswordForm.get('password')?.touched">
            Password must be at least 6 characters.
          </div>
        </div>

        <button
          type="submit"
          class="btn btn-primary login-btn"
          [disabled]="emailPasswordLoading">
          <span *ngIf="emailPasswordLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
          Sign in
        </button>

        <p *ngIf="!canEmailPassword" class="small text-muted mt-2 mb-0 text-start">
          To enable email sign-in and forgot-password, set <code>supabasePublishableKey</code> (or legacy
          <code>supabaseAnonKey</code>) in <code>runtime-config.json</code> or <code>runtime-config.local.json</code>.
        </p>
      </form>

      <div class="login-divider" *ngIf="brand.showSocialLogin" aria-hidden="true"><span>or</span></div>

      <div class="login-social mb-3" *ngIf="brand.showSocialLogin">
        @if (platform.isIos()) {
          <button
            type="button"
            class="btn login-social-btn login-social-apple"
            (click)="signInWithSocial('apple')"
            [disabled]="!runtime.oidcConfigured"
            aria-label="Sign in with Apple">
            <i class="bi bi-apple" aria-hidden="true"></i>
            <span>Apple</span>
          </button>
          <button
            type="button"
            class="btn login-social-btn login-social-google"
            (click)="signInWithSocial('google')"
            [disabled]="!runtime.oidcConfigured"
            aria-label="Sign in with Google">
            <i class="bi bi-google" aria-hidden="true"></i>
            <span>Google</span>
          </button>
        } @else {
          <button
            type="button"
            class="btn login-social-btn login-social-google"
            (click)="signInWithSocial('google')"
            [disabled]="!runtime.oidcConfigured"
            aria-label="Sign in with Google">
            <i class="bi bi-google" aria-hidden="true"></i>
            <span>Google</span>
          </button>
          <button
            type="button"
            class="btn login-social-btn login-social-apple"
            (click)="signInWithSocial('apple')"
            [disabled]="!runtime.oidcConfigured"
            aria-label="Sign in with Apple">
            <i class="bi bi-apple" aria-hidden="true"></i>
            <span>Apple</span>
          </button>
        }
        <div *ngIf="oidcClickError" class="alert alert-danger login-error mb-0" role="alert">
          <i class="bi bi-exclamation-circle login-error-icon" aria-hidden="true"></i>
          <div class="login-error-body">
            <p class="login-error-text mb-0">{{ oidcClickError }}</p>
          </div>
        </div>
      </div>

      <p class="small text-center mb-4">
        <a routerLink="/forgot-password">Forgot password?</a>
      </p>

      <p class="small text-center mb-4">
        New to {{ brand.productName }}? <a routerLink="/signup">Create account</a>
      </p>

      <details *ngIf="showEmergency" class="emergency-login mb-2">
        <summary class="small text-muted user-select-none">Emergency admin sign-in</summary>
        <form [formGroup]="emergencyForm" (ngSubmit)="onEmergencySubmit()" class="login-form mt-3">
          <div *ngIf="emergencyFailed" class="alert alert-danger login-error" role="alert">
            <i class="bi bi-exclamation-circle me-2"></i>Emergency credentials were rejected or login is disabled on the server.
          </div>

          <div class="form-group">
            <label for="emergency-username" class="form-label">Username</label>
            <input
              type="text"
              id="emergency-username"
              class="form-control"
              formControlName="username"
              placeholder="Emergency username"
              aria-label="Emergency username"
              autocomplete="username"
              [class.is-invalid]="emergencyForm.get('username')?.invalid && emergencyForm.get('username')?.touched" />
          </div>

          <div class="form-group">
            <label for="emergency-password" class="form-label">Password</label>
            <input
              type="password"
              id="emergency-password"
              class="form-control"
              formControlName="password"
              placeholder="Emergency password"
              aria-label="Emergency password"
              autocomplete="current-password"
              [class.is-invalid]="emergencyForm.get('password')?.invalid && emergencyForm.get('password')?.touched" />
          </div>

          <button
            type="submit"
            id="emergency-login-submit"
            class="btn btn-outline-secondary w-100 mt-2">
            Emergency log in
          </button>
        </form>
      </details>
      <app-legal-footer></app-legal-footer>
    </div>
  </div>

</div>
